export type AudioFactory = (src: string) => HTMLAudioElement;

const HAVE_ENOUGH_DATA = 4; // readyState constant for HTMLAudioElement

const waitForEvent = (target: EventTarget, event: string) =>
  new Promise<void>((resolve, reject) => {
    const onEvent = () => {
      cleanup();
      resolve();
    };
    const onError = (err?: Event | Error) => {
      cleanup();
      reject(err instanceof Error ? err : new Error('音频播放失败'));
    };
    const cleanup = () => {
      target.removeEventListener(event, onEvent);
      target.removeEventListener('error', onError);
    };
    target.addEventListener(event, onEvent, { once: true });
    target.addEventListener('error', onError, { once: true });
  });

// 使用 canplay 而不是 canplaythrough，减少等待时间
const HAVE_CURRENT_DATA = 2; // 有当前帧数据就可以播放
const waitForCanPlay = (audio: HTMLAudioElement) =>
  audio.readyState >= HAVE_CURRENT_DATA ? Promise.resolve() : waitForEvent(audio, 'canplay');

const waitForSeeked = (audio: HTMLAudioElement) => waitForEvent(audio, 'seeked');

export class SegmentAudioEngine {
  private cache = new Map<string, HTMLAudioElement>();
  private current?: HTMLAudioElement;
  private stopTimer?: number;
  private onTimeUpdate?: () => void;
  private createAudio: AudioFactory;

  constructor(options: { createAudio?: AudioFactory } = {}) {
    this.createAudio = options.createAudio ?? ((src: string) => new Audio(src));
  }

  private warmPromise?: Promise<void>;
  private warmedSrc?: string;
  private warmedStartMs?: number;

  async warm(src?: string, startMs?: number) {
    if (!src) return;
    this.warmedSrc = src;
    this.warmedStartMs = startMs;
    
    // 不等待加载完成，只启动加载过程
    this.warmPromise = (async () => {
      try {
        // 获取或创建 audio 元素并开始加载
        let audio = this.cache.get(src);
        if (!audio) {
          audio = this.createAudio(src);
          audio.preload = 'auto';
          this.cache.set(src, audio);
          audio.load?.();
        }
        // 预先 seek 到开始位置，减少首次播放延迟
        if (startMs !== undefined && startMs > 0) {
          audio.currentTime = startMs / 1000;
        }
      } catch (error) {
        console.warn('[audio] preload failed', error);
      }
    })();
    
    // 不阻塞，让预热在后台进行
    this.warmPromise.catch(() => {});
  }

  async playSegment(src: string, startMs: number, endMs: number): Promise<void> {
    if (!src) throw new Error('缺少音频地址');
    if (!Number.isFinite(startMs) || !Number.isFinite(endMs) || startMs < 0 || endMs <= startMs) {
      throw new Error('音频片段起止时间无效');
    }

    // 直接获取音频，不等待预热完成
    const audio = await this.loadFast(src);

    this.cleanupTimers();
    if (this.current && this.current !== audio) {
      this.current.pause();
    }
    this.current = audio;

    // 开始时间提前 50ms，避免开头被截断
    const startSec = Math.max(0, (startMs - 50) / 1000);
    // 给结束时间加 400ms 缓冲，避免最后一个单词被截断
    const bufferMs = 400;
    const endSec = (endMs + bufferMs) / 1000;

    // 如果已经预热到正确位置，直接播放
    const needSeek = Math.abs(audio.currentTime - startSec) > 0.1;
    
    if (needSeek) {
      audio.pause();
      audio.currentTime = startSec;
      // 不等待 seeked 事件，直接开始播放（浏览器会自动处理）
    }
    
    await audio.play();

    const onTimeUpdate = () => {
      if (audio.currentTime >= endSec - 0.01) {
        this.finishSegment(endSec);
      }
    };
    this.onTimeUpdate = onTimeUpdate;
    audio.addEventListener('timeupdate', onTimeUpdate);

    const durationMs = Math.max((endMs + bufferMs - startMs) / (audio.playbackRate || 1), 0);
    this.stopTimer = (globalThis.setTimeout(() => this.finishSegment(endSec), durationMs) as unknown as number);
  }

  stop() {
    if (!this.current) return;
    this.current.pause();
    this.cleanupTimers();
  }

  dispose() {
    this.stop();
    for (const audio of this.cache.values()) {
      audio.pause();
      audio.src = '';
    }
    this.cache.clear();
  }

  private cleanupTimers() {
    if (this.current && this.onTimeUpdate) {
      this.current.removeEventListener('timeupdate', this.onTimeUpdate);
    }
    if (this.stopTimer !== undefined) {
      globalThis.clearTimeout(this.stopTimer);
    }
    this.stopTimer = undefined;
    this.onTimeUpdate = undefined;
  }

  private finishSegment(endSec: number) {
    if (!this.current) return;
    this.current.pause();
    this.current.currentTime = endSec;
    this.cleanupTimers();
  }

  // 快速加载：只等待最小数据量
  private async loadFast(src: string): Promise<HTMLAudioElement> {
    let audio = this.cache.get(src);
    if (!audio) {
      audio = this.createAudio(src);
      audio.preload = 'auto';
      this.cache.set(src, audio);
      audio.load?.();
    }

    // 已有足够数据直接返回
    if (audio.readyState >= HAVE_CURRENT_DATA) return audio;

    // 使用超时：最多等待 800ms
    try {
      await Promise.race([
        waitForCanPlay(audio),
        new Promise((_, reject) => setTimeout(() => reject(new Error('timeout')), 800))
      ]);
    } catch (err) {
      // 超时也尝试播放（浏览器可能已缓冲足够数据）
      if ((err as Error).message !== 'timeout') {
        this.cache.delete(src);
        throw err instanceof Error ? err : new Error('音频加载失败');
      }
    }
    return audio;
  }

  private async load(src: string): Promise<HTMLAudioElement> {
    const cached = this.cache.get(src);
    if (cached) return cached;

    const audio = this.createAudio(src);
    audio.preload = 'auto';
    this.cache.set(src, audio);

    if (audio.readyState >= HAVE_CURRENT_DATA) return audio;

    const ready = waitForCanPlay(audio);
    audio.load?.();

    try {
      await ready;
      return audio;
    } catch (error) {
      this.cache.delete(src);
      throw error instanceof Error ? error : new Error('音频加载失败');
    }
  }
}

export const segmentAudioEngine = new SegmentAudioEngine();
